import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Button from '@artemis/components/Button';
import { FormattedMessage } from '@artemis/integrations/contentful/utils';
import { useEffectOnce } from 'react-use';
import ContactLink from './ContactLink';
import {
  Body,
  Bold,
  ButtonContainer,
  Container,
  PrimaryButton,
  Title,
  SecondaryButton,
} from './shared';
import { logCheckInboxImpression } from './analytics';

const EditButton = styled(Button)`
  display: inline;
  padding: 0;
  margin: 0;
  min-width: 0;
  > div {
    font-weight: ${props => props.theme.typography.fontWeightMedium};
  }
`;

const InlineBlock = styled.span`
  display: inline-block;
`;

const Email = styled(Bold)`
  overflow-wrap: anywhere;
`;

const TeamName = styled.span`
  font-style: italic;
  font-weight: ${props => props.theme.typography.fontWeightBold};
`;

const CheckInbox = ({
  teamName,
  email,
  isCheckStatusLoading,
  isResendLoading,
  onNext,
  onEditEmail,
  onSendVerificationEmail,
}) => {
  useEffectOnce(() => {
    logCheckInboxImpression({ email });
  });

  return (
    <Container>
      <Title>
        <FormattedMessage entry="teams.checkInbox.title" />
      </Title>

      {teamName && (
        <Body>
          <FormattedMessage
            entry="teams.checkInbox.joiningTeam"
            values={{ teamName: <TeamName>{teamName}</TeamName> }}
          />
        </Body>
      )}

      <Body>
        <FormattedMessage
          entry="teams.checkInbox.details.sent"
          values={{
            email: <Email>{email}</Email>,
          }}
        />
        <InlineBlock>
          <FormattedMessage
            entry="teams.checkInbox.editEmail"
            values={{
              editLink: text => (
                <EditButton onClick={onEditEmail}>{text}</EditButton>
              ),
            }}
          />
        </InlineBlock>
      </Body>

      <Body>
        <FormattedMessage
          entry="teams.checkInbox.details.openEmail"
          values={{ bold: text => <Bold>{text}</Bold> }}
        />
      </Body>

      <Body>
        <FormattedMessage
          entry="teams.checkInbox.contactLabel"
          values={{ link: text => <ContactLink>{text}</ContactLink> }}
        />
      </Body>

      <ButtonContainer>
        <PrimaryButton
          buttonType="solid"
          color="primary"
          isLoading={isCheckStatusLoading}
          isDisabled={isResendLoading}
          onClick={onNext}
        >
          <FormattedMessage entry="teams.checkInbox.continueButton" />
        </PrimaryButton>

        <SecondaryButton
          isDisabled={isCheckStatusLoading}
          isLoading={isResendLoading}
          onClick={() => onSendVerificationEmail(email)}
          color="black"
        >
          <FormattedMessage entry="teams.checkInbox.resendButton" />
        </SecondaryButton>
      </ButtonContainer>
    </Container>
  );
};

CheckInbox.propTypes = {
  teamName: PropTypes.string,
  email: PropTypes.string.isRequired,
  isCheckStatusLoading: PropTypes.bool,
  isResendLoading: PropTypes.bool,
  onNext: PropTypes.func.isRequired,
  onEditEmail: PropTypes.func.isRequired,
  onSendVerificationEmail: PropTypes.func.isRequired,
};

export default CheckInbox;
